<template>
  <mi-dialog
    :model-value="showModal"
    no-esc-dismiss
    no-backdrop-dismiss
    class="selectable-table-modal"
    :close-icon="false"
  >
    <div class="selectable-table-modal__header">
      <h4>{{ headerTitle }}</h4>
    </div>
    <div v-if="hasFilters" class="selectable-table-modal__filters row items-center">
      <mi-btn
        :class="filters.mineOnly && 'selectable-table-modal__filter-btn--selected'"
        flat
        :color="filters.mineOnly ? 'white' : 'primary'"
        @click="toggleMineOnly"
      >
        Mine only
      </mi-btn>
      <div class="selectable-table-modal__divider"></div>
      <div class="col">
        <mi-text-field
          v-model="searchField"
          bg-color="white"
          debounce="300"
          placeholder="Search"
          clearable
          hide-bottom-space
          outlined
          @update:model-value="fetchItems"
        >
          <template #prepend>
            <mi-icon name="search"></mi-icon>
          </template>
        </mi-text-field>
      </div>
    </div>
    <div class="selectable-table-modal__content">
      <mi-table
        ref="miTable"
        :columns="columns"
        :loading="tableLoading"
        :rows="rows"
        class="selectable-table-modal-table"
        column-sort-order="da"
        :loading-label="loadingLabel"
        :no-data-label="noDataLabel"
        row-key="id"
        hide-pagination
        sticky-header
      >
        <template #top>
          <span v-if="customHeader">{{ rows.length }} {{ itemsType }} </span>
          <mi-btn v-if="customHeader" outline @click="selectAllItems">Select all</mi-btn>
        </template>
        <template #body-cell-add="{ row }">
          <td>
            <mi-checkbox
              :model-value="isSelected(row.id)"
              class="q-pr-sm"
              @click="toggleSelection(row.id)"
            ></mi-checkbox>
          </td>
        </template>
      </mi-table>
      <div v-if="rows.length && hasFilters" class="add-configurations-to-list-modal__pagination">
        <mi-pagination
          :model-value="pagination.currentPage"
          :max="pagination.totalPages"
          :max-pages="pagination.maxNavPage"
          size="14px"
          boundary-links
          direction-links
          boundary-numbers
          ellipses
          @update:model-value="handlePaginationChange"
        ></mi-pagination>
      </div>
    </div>

    <template #actions>
      <div class="q-mb-md">
        <mi-btn flat @click="closeModal">Cancel</mi-btn>
        <mi-btn
          :loading="loadingConfirm"
          color="accent"
          :disable="disableConfirm"
          @click="onHandleConfirmAction"
        >
          {{ confirmBtnText }}
        </mi-btn>
      </div>
    </template>
  </mi-dialog>
</template>

<script>
  import { computed, ref, watch } from 'vue'
  import MiCheckbox from '@/packages/@mi-library/MiCheckbox/MiCheckbox.vue'

  export default {
    name: 'SelectableTableModal',
    components: { MiCheckbox },
    props: {
      showModal: {
        type: Boolean,
        default: false
      },
      disableConfirm: {
        type: Boolean,
        default: false,
        required: false
      },
      headerTitle: {
        type: String,
        default: '',
        required: false
      },
      loadingLabel: {
        type: String,
        default: 'Loading lists. Please wait ...',
        required: false
      },
      noDataLabel: {
        type: String,
        default: 'No lists found. Please create a new one.',
        required: false
      },
      tableLoading: {
        type: Boolean,
        default: false,
        required: false
      },
      loadingConfirm: {
        type: Boolean,
        default: false,
        required: false
      },
      customHeader: {
        type: Boolean,
        default: false,
        required: false
      },
      itemsType: {
        type: String,
        default: '',
        required: false
      },
      rows: {
        type: Array,
        default: () => [],
        required: true
      },
      columns: {
        type: Array,
        required: true,
        default: () => []
      },
      handleDeselectAll: {
        type: Function,
        required: false,
        default: () => ({})
      },
      handlePaginationChange: {
        type: Function,
        required: false,
        default: () => ({})
      },
      onHandleConfirm: {
        type: Function,
        required: true,
        default: () => ({})
      },
      confirmBtnText: {
        type: String,
        required: false,
        default: 'Confirm'
      },
      hasFilters: {
        type: Boolean,
        default: false,
        required: false
      },
      fetchItems: {
        type: Function,
        required: true,
        default: () => ({})
      },
      toggleMineOnly: {
        type: Function,
        required: true,
        default: () => ({})
      },
      filters: {
        type: Object,
        required: false,
        default: () => {}
      },
      pagination: {
        type: Object,
        required: false,
        default: () => {}
      }
    },
    emits: ['update:show-modal', 'update:listsSelected'],
    setup(props, { emit }) {
      const itemIdSelected = ref([])
      const searchField = ref('')
      const itemIdSelectedSet = computed(() => new Set(itemIdSelected.value))

      const isSelected = id => itemIdSelectedSet.value.has(id)

      const addSelection = id => {
        if (!itemIdSelectedSet.value.has(id)) {
          itemIdSelected.value = [...itemIdSelected.value, id]
        }
      }

      const removeSelection = id => {
        if (itemIdSelectedSet.value.has(id)) {
          itemIdSelected.value = itemIdSelected.value.filter(
            selectedId => selectedId !== id
          )
        }
      }

      const toggleSelection = id => {
        if (itemIdSelectedSet.value.has(id)) removeSelection(id)
        else addSelection(id)
      }

      const selectAllItems = () => {
        props.rows.forEach(row => {
          if (!itemIdSelectedSet.value.has(row.id)) {
            addSelection(row.id)
          }
        })
      }

      const deselectAllItems = () => {
        itemIdSelected.value = []
      }

      const closeModal = () => {
        emit('update:show-modal')
        deselectAllItems()
      }

      const onHandleConfirmAction = () => {
        props.onHandleConfirm(itemIdSelected.value)
      }

      watch(() => itemIdSelected.value, () => {
        emit('update:listsSelected', itemIdSelected.value)
      })

      return {
        onHandleConfirmAction,
        closeModal,
        isSelected,
        toggleSelection,
        selectAllItems,
        searchField
      }
    }
  }
</script>

<style lang="scss">
  //stylelint-disable selector-max-compound-selectors, declaration-no-important
  .selectable-table-modal {
    .q-dialog__inner > .mi-card {
      width: 769px;
      max-width: 769px;
      max-height: calc(100vh - 200px);
      background: $mi-silver-50;
      height: 571px;
    }

    &__filters {
      padding: 0 20px;
      gap: 12px;
    }

    &__divider {
      border: 1px solid $mi-anthracite-50;
      height: 27px;
    }

    &__filter-btn {
      &--selected {
        background-color: $mi-anthracite-800;
      }
    }

    .mi-table-wrapper .q-table__bottom--nodata {
      height: 200px;
    }

    .mi-table__header {
      background: $mi-silver-50 !important;
    }

    &__header {
      h4 {
        padding: 8px 12px;
        margin: 12px 0;
        background: $mi-white;
      }
    }

    .q-card__section {
      padding: 20px;
    }

    .q-table__top {
      background: $mi-silver-50 !important;
      font-family: "Man Europe", sans-serif;
      font-weight: 500;
      font-size: 12px;
      line-height: 21px;
      letter-spacing: 0;
      color: #303c49;
      justify-content: space-between;

      .span {
        padding: 10px;
      }

      .mi-btn {
        font-size: 14px;
      }
    }

    &__content {
      padding-top: 20px;
    }

    .mi-card__actions {
      padding-top: 4px;
      padding-right: 24px;
      height: 6vh;
    }

    .selectable-table-modal-table {
      max-height: calc(100vh - 450px);
    }

    .q-card__section:first-child {
      height: 88%;
    }

    .mi-table-wrapper .mi-table tbody td {
      border-bottom: 1px solid #c5c5c5;
    }

    .mi-table-wrapper .mi-table .mi-table__header th {
      border-bottom: 1px solid #c5c5c5;
      font-weight: 500;
    }

    .mi-table-wrapper .mi-table tbody tr td:first-child {
      border-left: 1px solid #c5c5c5;
      text-align-last: center;
    }

    .mi-table-wrapper .mi-table tbody tr td:last-child {
      border-right: 1px solid #c5c5c5;
    }

    .q-inner-loading {
      height: 320px;
    }
  }
</style>
